import axiosInstance from '../../../lib/axios';
import { putToStorage } from '../../../lib/uploads';
import { CASES_API_URL } from '../../../config/env';
import { getAccessToken } from '../../../lib/tokenStore';

/**
 * Returns the URL for the document stream endpoint.
 *
 * The stream endpoint proxies the document bytes from B2 through our own
 * backend, so the browser fetches from our domain (where nginx already sets
 * Access-Control-Allow-Origin) instead of directly from B2 (which has no
 * CORS rules configured).
 *
 * @param {string} mediaId - the CaseMedia DB row id
 */
export const getDocumentStreamUrl = (mediaId) => {
  const token = getAccessToken();
  const base = `${CASES_API_URL}/media/stream/${mediaId}`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
};

/**
 * Fetches a document from the stream endpoint as an ArrayBuffer using our configured
 * axios instance (which automatically injects JWT credentials, CSRF headers, and
 * handles silent token refresh + retry on 401).
 *
 * Used by the DOCX viewer (mammoth) for in-browser Word doc rendering.
 *
 * @param {string} mediaId - the CaseMedia DB row id
 * @returns {Promise<ArrayBuffer>}
 */
export const fetchDocumentAsArrayBuffer = async (mediaId) => {
  const response = await axiosInstance.get(`${CASES_API_URL}/media/stream/${mediaId}`, {
    responseType: 'arraybuffer',
  });
  return response.data;
};

/**
 * Normalizes legacy local-upload URLs to the gateway port (:81).
 *
 * Media now lives in B2 and the API returns signed URLs, which pass through
 * untouched — as do the blob: URLs used to preview a file before it is
 * uploaded. This only still matters for rows written before the B2 migration,
 * where a host header without a port was saved into the database.
 */
export const formatMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return url || '';
  if (url.startsWith('http://localhost/uploads/') || url.startsWith('http://127.0.0.1/uploads/')) {
    return url.replace(/^http:\/\/(localhost|127\.0\.0\.1)\/uploads\//, 'http://localhost:81/uploads/');
  }
  if (url.includes(':5005/uploads/')) {
    return url.replace(':5005/uploads/', ':81/uploads/');
  }
  return url;
};

/**
 * Uploads one file straight to Backblaze B2, bypassing our backend.
 *
 * Two steps: ask our API for a short-lived signed URL, then PUT the bytes to B2
 * with it. The file never passes through our server, so a 200 MB video does not
 * occupy a backend process or hit the load balancer's idle timeout.
 *
 * The returned `url` is a STAGING key (`tmp/…`), not a fetchable address. Submit
 * it with the case and the server verifies the object, moves it to its permanent
 * key, and stores that. Anything never submitted is swept by the bucket's
 * lifecycle rule on the tmp/ prefix.
 *
 * @param {File} file - The file to upload
 * @param {string} section - One of: CHIEF_COMPLAINT, CLINICAL_FINDINGS, DIAGNOSTICS, OUTCOME, GENERAL
 * @param {(percent: number) => void} [onProgress] - Upload progress, 0-100
 * @returns {Promise<{url: string, filename: string, type: string, section: string, mimeType: string, sizeBytes: number}>}
 */
/**
 * Signs a short-lived preview for a file already uploaded but not yet submitted.
 *
 * Needed when a saved draft is reopened: the storage key survived in
 * localStorage, but the `blob:` preview belonged to the page that created it and
 * is gone. The server checks the key is the caller's own staged upload, pins the
 * response content type, and expires the link in ten minutes.
 *
 * @param {string} key - a `tmp/case/media/...` staging key
 * @returns {Promise<{url: string, mimeType: string, type: 'IMAGE'|'VIDEO'}>}
 */
export const presignStagedPreview = async (key) => {
  const { data } = await axiosInstance.post(`${CASES_API_URL}/media/preview`, { key });
  return data?.data ?? null;
};

export const uploadCaseMedia = async (file, section, onProgress) => {
  const { data } = await axiosInstance.post(`${CASES_API_URL}/media/presign`, {
    filename: file.name,
    contentType: file.type,
    size: file.size,
    section,
  });
  const { key, uploadUrl, type } = data?.data ?? {};
  if (!uploadUrl) throw new Error('Could not get an upload URL. Please try again.');

  // Shared with lib/uploads.js: raw axios (our interceptors would break the
  // signature) plus a retry for the intermittent 500s B2 returns under load.
  // That matters most here — a video may be 200 MB, and without a retry a
  // single transient fault throws away the entire transfer.
  await putToStorage(uploadUrl, file, onProgress);

  return {
    url: key,
    filename: file.name,
    type,
    section,
    mimeType: file.type,
    sizeBytes: file.size,
  };
};

/**
 * Delete a case media item by its DB record ID.
 */
export const deleteCaseMedia = async (mediaId) => {
  const response = await axiosInstance.delete(`${CASES_API_URL}/media/${mediaId}`);
  return response.data;
};
